/**
 * This service should handle scheduling of api calls that update line items in the cart.
 *
 * The rules are as follows:
 * - There can be up to six update and add calls in parallel
 * - If a delete call is scheduled, it needs to wait for all calls currently in progress to finish
 * - If a delete call is scheduled, all new calls need to wait for the delete call to finish
 * - Delete calls are run before update and add calls
 */

const MAX_PARALLEL_CALLS = 6;

type CallTypes = "add" | "update" | "delete" | "deleteCart" | "cartView" | "miniCart";

export interface CallProcessorDefinition<T> {
  type: CallTypes;
  execute: (data: T) => Promise<Response>;
  canBeExecuted: (ongoingCalls: QueuedCall[]) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callProcessors: Record<string, CallProcessorDefinition<any>> = {};

export function registerCallProcessor<T>(definition: CallProcessorDefinition<T>) {
  callProcessors[definition.type] = definition;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function scheduleCall(type: CallTypes, data: any): Promise<Response> {
  return new Promise((resolve, reject) => {
    const call = { type, data, resolve, reject };
    callQueue.push(call);
    processQueue();
  });
}

export type QueuedCall = {
  type: CallTypes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: (data: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reject: (error: any) => void;
};

const callQueue: QueuedCall[] = [];
const ongoingCallsCartService: QueuedCall[] = [];

function processQueue() {
  while (ongoingCallsCartService.length < MAX_PARALLEL_CALLS && callQueue.length > 0) {
    const nextCall = callQueue[0]!;

    const processor = callProcessors[nextCall.type];
    if (!processor) {
      console.error("No processor found for call type", nextCall.type);
      callQueue.shift();
    }

    if (processor.canBeExecuted(ongoingCallsCartService)) {
      execute(nextCall);
      callQueue.shift();
    }

    if (nextCall === callQueue[0]) {
      return;
    }
  }
}

function execute(call: QueuedCall) {
  ongoingCallsCartService.push(call);

  callProcessors[call.type]
    .execute(call.data)
    .then((data) => {
      call.resolve(data);
    })
    .catch((error) => {
      call.reject(error);
    })
    .finally(() => {
      ongoingCallsCartService.splice(ongoingCallsCartService.indexOf(call), 1);
      processQueue();
    });
}
